import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTracking } from 'react-tracking';
import useAnalytics from '@app/hooks/useAnalytics';
import {
  getPONumberSubmitEventForPendingQuoteDetails,
  getPoSubmitEventForFieldValidationByFormikErrors
} from '@app/components/pages/summary-and-payment/analyticsUtils';
import { getValueOnCondition } from '@app/components/pages/account/common/utils';
import {
  CUSTOMERS_SELECTED_TEXT,
  SUMMARY_AND_PAYMENT_FORM_LOCATION,
  PLACE_ORDER_FORM_NAME,
  SUMMARY_AND_PAYMENT_CREATE_QUOTE,
  SEND_QUOTE_TO_CUSTOMER_FORM_NAME
} from '../../analyticsConstants';
import {
  STATUS_FAIL,
  STATUS_SUCCESS,
  SYSTEM_ERROR
} from '@app/constants/analyticsConstants';
import { getGACheckoutProducts } from '@app/hooks/useAnalytics/utils';
import { convertFormattedNumberToUnformatted } from '@app/components/pages/checkout/utils';

const useGATracking = () => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const { fireFormSubmittedEvent, fireAddPaymentInfoEvent } = useAnalytics();
  const dealerDCNAssociations = useSelector(
    s => s?.checkout?.dealerDCNAssociations
  );
  const isIIBFail = useSelector(s => s?.checkout?.isIIBFail);
  const dealerCode = useSelector(s => s.dealer?.dealerCode);
  const dcnName = useSelector(s => s?.common?.dcnName) ?? '';
  const [number, name] = dcnName.split('-');
  const dcnNameNumber = getValueOnCondition(
    name && number,
    `${name}-${number}`,
    null
  );

  const getAddPaymentInfoEventObject = currentOrder => {
    const {
      billingMethod,
      currencyNotation,
      ascendingOrderItems,
      orderTotals,
      orderExtendAttribute,
      financeOptionsSelected
    } = currentOrder;

    const gaProducts = getGACheckoutProducts({
      products: ascendingOrderItems || []
    });

    const orderTotal =
      orderTotals?.unformattedOrderSubTotal || orderTotals?.orderSubTotal
        ? convertFormattedNumberToUnformatted(orderTotals?.orderSubTotal)
        : 0;
    return {
      billingMethod,
      currencyNotation,
      products: gaProducts,
      list: '',
      orderTotal,
      dealerStore: orderExtendAttribute?.dealerStore,
      financeOptionsSelected
    };
  };

  const getFormSubmitEventObject = ({
    isPlaceOrderFailed,
    selectedDealerPlaceOrder = '',
    sendToQuotePlaceOrder,
    gaFormName = ''
  }) => {
    const placeOrderErrorCheck =
      isPlaceOrderFailed &&
      dealerDCNAssociations &&
      !!dealerDCNAssociations.length;
    const selectedDealerPlaceOrderLength = getValueOnCondition(
      sendToQuotePlaceOrder,
      selectedDealerPlaceOrder.length,
      0
    );
    const systemError = getValueOnCondition(isPlaceOrderFailed, SYSTEM_ERROR);
    const formFieldCausingError = getValueOnCondition(
      placeOrderErrorCheck,
      `${selectedDealerPlaceOrderLength} ${CUSTOMERS_SELECTED_TEXT}`,
      systemError
    );
    const isPlaceOrderAction =
      gaFormName ===
      (PLACE_ORDER_FORM_NAME || SUMMARY_AND_PAYMENT_CREATE_QUOTE);
    return {
      pagePath: document.location.pathname,
      formName: gaFormName,
      formContent: getValueOnCondition(!isPlaceOrderAction, dcnNameNumber),
      formContent2: getValueOnCondition(
        gaFormName === SEND_QUOTE_TO_CUSTOMER_FORM_NAME,
        `${selectedDealerPlaceOrderLength} ${CUSTOMERS_SELECTED_TEXT}`
      ),
      formFieldCausingError: formFieldCausingError,
      formStatus: getValueOnCondition(
        !isIIBFail && !isPlaceOrderFailed,
        STATUS_SUCCESS,
        STATUS_FAIL
      ),
      formLocation: getValueOnCondition(
        !isPlaceOrderAction,
        SUMMARY_AND_PAYMENT_FORM_LOCATION
      )
    };
  };

  const trackFormSubmission = ({
    isPlaceOrderFailed,
    selectedDealerPlaceOrder,
    sendToQuotePlaceOrder,
    gaFormName
  }) =>
    fireFormSubmittedEvent(
      getFormSubmitEventObject({
        isPlaceOrderFailed,
        selectedDealerPlaceOrder,
        sendToQuotePlaceOrder,
        gaFormName
      })
    );

  const trackFormError = (payload, gaFormName) => {
    const gaEvent = getPoSubmitEventForFieldValidationByFormikErrors({
      payload,
      errorFieldKeys: {
        poNumberError: t('PO_NUMBER_REQ'),
        poAttachmentError: t('PO_ATTACHMENT_REQ'),
        poNumberAttachmentError: t('PO_NUMBER_ATTACHMENT_REQ')
      },
      gaFormName
    });
    trackEvent(gaEvent);
  };

  const trackPOFormSubmission = (
    POChanged,
    quoteNumber,
    status,
    error,
    gaFormName
  ) => {
    const gaValues = {
      dealerCode,
      quoteNumber,
      POChanged,
      gaFormName
    };
    trackEvent(
      getPONumberSubmitEventForPendingQuoteDetails({
        ...gaValues,
        status: status,
        error: error
      })
    );
  };

  const trackAddPaymentInfo = currentOrder => {
    fireAddPaymentInfoEvent(getAddPaymentInfoEventObject(currentOrder));
  };

  return {
    trackFormSubmission,
    trackFormError,
    trackPOFormSubmission,
    trackAddPaymentInfo
  };
};

export default useGATracking;
